import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../components/ui/Button";
import Logo from "../components/Logo";

const FEATURES = [
  {
    icon: "trophy",
    title: "One place for every hackathon",
    body: "Stop juggling spreadsheets and Discord pings. Track registration, submissions, prizes, and outcomes in one calm workspace."
  },
  {
    icon: "alarm",
    title: "Reminders that actually fire",
    body: "Per-hackathon reminder offsets that work for solo builders and teams. In-app, email, and optional browser push."
  },
  {
    icon: "view_kanban",
    title: "Milestones, not vibes",
    body: "Break each project into stages. See instantly what's at risk so you ship before the timer hits zero."
  },
  {
    icon: "analytics",
    title: "Insights on your runs",
    body: "Win rate by domain, platform, and team size. Spot patterns that actually help you pick what to enter next."
  },
  {
    icon: "share",
    title: "Share with your team",
    body: "One-tap shareable hackathon links so your teammates import the whole tracker, deadlines and all."
  },
  {
    icon: "lock",
    title: "Private by default",
    body: "Your hackathon data lives in your account. Row-level security on every table. No public profiles unless you flip the switch."
  }
];

const STEPS = [
  { num: "01", title: "Sign in", body: "Free with Google or email & password. No card." },
  { num: "02", title: "Add hackathons", body: "Paste a link, paste raw rules text, or fill in the fields." },
  { num: "03", title: "Stay ahead", body: "Get pinged before each deadline. Log the outcome when it's done." }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  return (
    <div className="min-h-dvh bg-surface text-ink">
      <header className="sticky top-0 z-40 bg-surface/85 backdrop-blur border-b border-edge">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 font-semibold">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-brand-subtle text-brand">
              <Logo className="w-5 h-5" />
            </span>
            <span className="font-display text-lg tracking-tight">JustTrackIt</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-ink-muted">
            <a href="#features" className="hover:text-ink transition-colors">Features</a>
            <a href="#how" className="hover:text-ink transition-colors">How it works</a>
            <a href="#faq" className="hover:text-ink transition-colors">FAQ</a>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" size="md" onClick={() => navigate("/login")}>Sign in</Button>
            <Button variant="primary" size="md" iconEnd="arrow_forward" onClick={() => navigate("/login")}>
              Start free
            </Button>
          </div>
          <button
            type="button"
            className="md:hidden p-2 -mr-2 text-ink-muted hover:text-ink rounded-md focus-visible:ring-2 focus-visible:ring-brand/40 outline-none"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            <span className="material-symbols-outlined text-[26px]">{mobileOpen ? "close" : "menu"}</span>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-edge bg-surface-overlay animate-fade-in">
            <div className="px-4 py-4 space-y-1 max-w-page mx-auto">
              {[
                { href: "#features", label: "Features" },
                { href: "#how", label: "How it works" },
                { href: "#faq", label: "FAQ" }
              ].map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-3 rounded-lg text-base font-medium text-ink hover:bg-surface-raised"
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-3 grid grid-cols-2 gap-2">
                <Button variant="secondary" onClick={() => navigate("/login")}>Sign in</Button>
                <Button variant="primary" iconEnd="arrow_forward" onClick={() => navigate("/login")}>
                  Start free
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>
        <section className="relative overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(70rem 40rem at 50% -10%, rgb(var(--brand) / 0.18), transparent 60%), radial-gradient(40rem 30rem at 100% 0%, rgb(var(--brand) / 0.10), transparent 60%)"
            }}
          />
          <div className="relative max-w-page mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 lg:pt-28 pb-16 lg:pb-24 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs bg-brand-subtle text-brand border border-brand/30 font-medium">
              <span className="material-symbols-outlined text-[14px]">bolt</span>
              Built for student hackers
            </div>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ink">
              Track every hackathon
              <span className="block text-brand">in one calm place.</span>
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base sm:text-lg text-ink-muted leading-relaxed">
              Deadlines, milestones, ideas, and outcomes — without the spreadsheet chaos. Free forever for the core tracker.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button variant="primary" size="lg" iconEnd="arrow_forward" onClick={() => navigate("/login")}>
                Start tracking free
              </Button>
              <a
                href="#features"
                className="text-sm font-medium text-ink-muted hover:text-ink transition-colors px-3 py-2 inline-flex items-center gap-1.5"
              >
                See what's inside
                <span className="material-symbols-outlined text-[18px]">arrow_downward</span>
              </a>
            </div>
            <p className="mt-4 text-xs text-ink-subtle">No credit card. Cancel anytime.</p>
          </div>
        </section>

        <section id="features" className="max-w-page mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-widest text-brand font-semibold">Why JustTrackIt</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3 leading-tight">
              The things spreadsheets never did.
            </h2>
            <p className="mt-3 text-base text-ink-muted">
              Designed for the way student teams actually run hackathons — late nights, fast pivots, and one missed deadline that ruins everything.
            </p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div key={f.title} className="obsidian-card p-5 sm:p-6 h-full">
                <div className="h-10 w-10 rounded-xl bg-brand-subtle text-brand grid place-items-center mb-4">
                  <span className="material-symbols-outlined text-[22px]">{f.icon}</span>
                </div>
                <h3 className="font-display text-lg font-semibold">{f.title}</h3>
                <p className="text-sm text-ink-muted mt-1.5 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="how" className="bg-surface-subtle border-y border-edge">
          <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-widest text-brand font-semibold">How it works</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3 leading-tight">
                Three steps. Then your deadlines find you.
              </h2>
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {STEPS.map((s) => (
                <div key={s.num} className="obsidian-card p-6">
                  <p className="font-mono text-sm text-brand">{s.num}</p>
                  <h3 className="font-display text-lg font-semibold mt-2">{s.title}</h3>
                  <p className="text-sm text-ink-muted mt-1.5">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="max-w-page mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-widest text-brand font-semibold">FAQ</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3 leading-tight">
              Common questions.
            </h2>
          </div>
          <div className="mt-10 grid gap-3 sm:grid-cols-2 max-w-4xl">
            {[
              {
                q: "Is it free?",
                a: "The whole tracker is free. We may add optional paid extras later but the core stays free for students."
              },
              {
                q: "Can my team see my hackathons?",
                a: "Only if you create a share link for a specific hackathon and send it to them. Nothing is public by default."
              },
              {
                q: "Does it work on phones?",
                a: "Yes. JustTrackIt is mobile-first with a bottom tab bar, and it installs as a PWA on iOS and Android."
              },
              {
                q: "What data do you store?",
                a: "Only what you enter. We never scrape your email or import contacts. Tokens never leave your device unencrypted."
              }
            ].map((item) => (
              <details
                key={item.q}
                className="obsidian-card p-5 sm:p-6 group [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex items-start justify-between gap-3 cursor-pointer">
                  <h3 className="font-semibold text-ink">{item.q}</h3>
                  <span className="material-symbols-outlined text-ink-muted shrink-0 transition-transform group-open:rotate-180">
                    expand_more
                  </span>
                </summary>
                <p className="text-sm text-ink-muted mt-3 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="max-w-page mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="relative overflow-hidden obsidian-card p-8 sm:p-12 text-center">
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(40rem 24rem at 50% 100%, rgb(var(--brand) / 0.15), transparent 60%)"
              }}
            />
            <h2 className="relative font-display text-3xl sm:text-4xl font-bold">
              Stop missing deadlines.
            </h2>
            <p className="relative mt-3 text-base text-ink-muted max-w-md mx-auto">
              Sign in and add your first hackathon in under a minute.
            </p>
            <div className="relative mt-7">
              <Button
                variant="primary"
                size="lg"
                iconEnd="arrow_forward"
                onClick={() => navigate("/login")}
              >
                Get started — free
              </Button>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-edge bg-surface-subtle">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-ink-muted">
            <span className="grid place-items-center w-7 h-7 rounded-lg bg-brand-subtle text-brand">
              <Logo className="w-4 h-4" />
            </span>
            <span>JustTrackIt — made for student hackers.</span>
          </div>
          <div className="flex gap-5 text-sm text-ink-muted">
            <a href="#features" className="hover:text-ink">Features</a>
            <a href="#how" className="hover:text-ink">How</a>
            <a href="#faq" className="hover:text-ink">FAQ</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
